import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import {
  AuthService,
  derivePrimaryRole,
  getAssignableUserRolesForEditor,
  normalizeUserRoles,
} from '@nimmsta/ng-commons-lib/auth';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import {
  InputWithErrorComponent,
  SaveableFormComponent,
} from '@nimmsta/ng-commons-lib/forms';
import { Organization, User, UserRole } from '@nimmsta/ng-commons-lib/models';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OrganizationUserService } from '@nimmsta/ng-b2b-lib/services/organization-user';
import { MultiSelect } from 'primeng/multiselect';

@Component({
  selector: 'app-partner-user-form',
  templateUrl: 'partner-user-form.component.html',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    InputWithErrorComponent,
    ErrorComponent,
    MultiSelect,
  ],
})
export class PartnerUserFormComponent extends SaveableFormComponent<User> {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);

  protected override dataService = inject(OrganizationUserService);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  @Input() allUserTable = false;

  assignableRoles = this.createRoleOptions([UserRole.MANAGER]);

  allOrgs: Organization[] = [];

  orgs: Organization[] = [];

  secondaryOrgs: string[] = [];

  oldSecondaryOrgs: string[] = [];

  constructor() {
    super();
    this.setValue({} as User);
  }

  override setValue(value: User): void {
    super.setValue(value);
    if (value.id) {
      this.form.controls['email'].disable();
    } else {
      this.form.controls['email'].enable();
    }
    this.authService.getCurrentUser().subscribe((user) => {
      const assignableRoles = this.getAssignableRoles(user.role);
      this.assignableRoles = this.createRoleOptions(assignableRoles);
    });
    if (this.allUserTable) {
      this.orgs = this.allOrgs.filter(org => org.id !== value.organization.id);
      this.secondaryOrgs = value.secondaryOrganizations ? value.secondaryOrganizations.map(org => org.id) : [];
      this.oldSecondaryOrgs = value.secondaryOrganizations ? value.secondaryOrganizations.map(org => org.id) : [];
    }
  }

  static passwordRepeat(form: AbstractControl): ValidationErrors | null {
    const passwordControl = form.get('password');
    const passwordRepeatControl = form.get('repeat_password');
    const message = {
      not_matching: '',
    };
    if (
      passwordControl?.value !== passwordRepeatControl?.value &&
      (form.value.id === 0 || passwordControl?.value !== '')
    ) {
      passwordRepeatControl?.setErrors(message);
    }
    return null;
  }

  protected setFormGroup(): void {
    const visibleRoles = this.getVisibleRoles(this.value.roles ?? [this.value.role]);
    this.form = this.fb.group({
      id: this.value.id,
      email: [this.value.email, [Validators.required, Validators.email]],
      given_name: [
        this.value.given_name,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(30),
        ],
      ],
      family_name: [
        this.value.family_name,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(30),
        ],
      ],
      roles: [visibleRoles],
    });
  }

  protected getValidators(): ValidatorFn[] {
    if (this.value.id === '') {
      return [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(25),
      ];
    }
    return [];
  }

  override onSave(): void {
    if (this.form.pristine && this.form.valid && JSON.stringify(this.secondaryOrgs) === JSON.stringify(this.oldSecondaryOrgs)) {
      this.closeEvent.emit();
      return;
    }
    if (this.form.valid) {
      this.error = undefined;
      this.loadingEvent.emit(true);
      const originalRoles = normalizeUserRoles(this.value.roles ?? [this.value.role]);
      this.value = Object.assign(this.value, this.form.value);
      const rolesValue = this.form.getRawValue()['roles'];
      const selectedRoles = Array.isArray(rolesValue)
        ? rolesValue as Array<UserRole | string | null | undefined>
        : [rolesValue as UserRole | string | null | undefined];
      const normalizedSelectedRoles = normalizeUserRoles(selectedRoles);
      this.value.roles = this.value.id && this.haveSameRoles(originalRoles, normalizedSelectedRoles)
        ? undefined
        : normalizedSelectedRoles;
      this.value.role = derivePrimaryRole(this.value.roles ?? originalRoles);
      this.form.disable();
      this.applyCustomValues();
      this.dataService.createOrUpdate(this.value).subscribe({
        next: (value) => {
          this.value = value;
          this.closeEvent.emit();
          this.form.enable();
          this.form.markAsPristine();
          this.loadingEvent.emit(false);
        },
        error: (response) => {
          this.error = response;
          this.form.enable();
          this.loadingEvent.emit(false);
        },
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  private getVisibleRoles(
    roles: Array<UserRole | string | null | undefined> | null | undefined,
  ): UserRole[] {
    return normalizeUserRoles(roles).filter((role) => role !== UserRole.USER);
  }

  private getAssignableRoles(editorRole: UserRole | string | null | undefined): UserRole[] {
    return getAssignableUserRolesForEditor(editorRole);
  }

  private createRoleOptions(roles: UserRole[]): Array<{ label: string, value: UserRole }> {
    return roles.map((role) => ({
      label: this.translate.instant(role),
      value: role,
    }));
  }

  private haveSameRoles(left: UserRole[], right: UserRole[]): boolean {
    return left.length === right.length && left.every((role, index) => role === right[index]);
  }
}
